import { Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, of, switchMap } from 'rxjs';

import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { createServerQuery } from '@/components/data-table-builder/server-query';
import { ReportsService } from '../reports/reports.service';
import { ReportAdditifResponseDto } from '@/types';
import { getReportRequestDataTableStructure } from './utils/report-request-data-table-object';

@Component({
  selector: 'sm-report-request',
  standalone: true,
  imports: [CommonModule, PageHeaderComponent, DataTableBuilderComponent, TranslateModule],
  templateUrl: './report-request.component.html',
  styleUrl: './report-request.component.scss',
})
export class ReportRequestComponent implements OnInit, OnDestroy {
  router = inject(Router);
  translate = inject(TranslateService);
  messageService = inject(MessageService);
  reportsService = inject(ReportsService);

  breadcrumb = [
    {
      label: 'clarification.report_request.plural',
      routerLink: ['/report-request'],
    }
  ];

  rowActionsFn = (row: ReportAdditifResponseDto): DataTableRowActions => ({
    defaultActions: {
      inspect: {
        label: 'clarification.actions.consult',
      },
      edit: {
        label: 'clarification.actions.treat',
      },
    },
    inspectAction: () => {
      this.router.navigate([`/report-request/${row.id}`], { queryParams: { mode: 'consult' } });
    },
    editAction: () => {
      this.router.navigate([`/report-request/${row.id}`], { queryParams: { mode: 'edit' } });
    },
  });

  data: ReportAdditifResponseDto[] = [];
  loading = true;
  totalRecords = 0;
  serverQuery = createServerQuery({ initialSortBy: 'id', initialSortOrder: 'desc' });

  dataTableObject?: DynamicDataTable;

  effectiveQuery = computed(() => {
    const rawSortBy = this.serverQuery.sortBy();
    const rawSortOrder = this.serverQuery.sortOrder();

    const sortBy = rawSortBy || 'id';
    const sortOrder = rawSortBy ? (rawSortOrder || 'desc') : 'desc';

    return {
      page: this.serverQuery.page(),
      size: this.serverQuery.pageSize(),
      search: this.serverQuery.search(),
      sort: `${sortBy},${sortOrder}`,
    };
  }, {
    equal: (a, b) => JSON.stringify(a) === JSON.stringify(b)
  });

  constructor() {
    toObservable(this.effectiveQuery)
      .pipe(
        debounceTime(50),
        switchMap(params => {
          this.loading = true;
          return this.reportsService.getPaginatedReports(params).pipe(
            catchError(err => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: err.error?.message || 'Error loading report requests',
              });
              return of(null);
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe(response => {
        if (response) {
          this.data = response.content ?? [];
          this.totalRecords = response.totalElements ?? 0;
        }
        this.loading = false;
      });
  }

  ngOnInit(): void {
    this.initTable();
    this.translate.onLangChange.subscribe(() => {
      this.initTable();
    });
  }

  initTable(): void {
    this.dataTableObject = getReportRequestDataTableStructure({
      translate: (key: string) => this.translate.instant(key),
      rowActionsFn: this.rowActionsFn,
      serverQuery: this.serverQuery,
    });
  }

  ngOnDestroy(): void {}
}

